/* eslint-disable compat/compat */
import { useCallback, useMemo } from "react";

import { LogSentryException } from "@shapr3d/frontend-lib";

import OpfsWorker from "../../workers/OPFSWorker?worker";
import { MODEL_VIEWER_DIRECTORY, type WorkerResponse } from "../../workers/types";

export interface UseOPFSResult {
  isSupported: boolean;
  readFile: (fileName: string) => Promise<File | null>;
  deleteFile: (fileName: string) => Promise<void>;
  streamUrlToFile: (
    fileName: string,
    url: string,
    onProgress?: (progress: number) => void
  ) => Promise<File | null>;
}

type PendingTask = {
  resolve: () => void;
  reject: (err: Error) => void;
  onProgress?: (percent: number) => void;
};

async function getOPFSDirectory(): Promise<FileSystemDirectoryHandle> {
  const root = await navigator.storage.getDirectory();
  return root.getDirectoryHandle(MODEL_VIEWER_DIRECTORY, { create: true });
}

const pending = new Map<string, PendingTask>();
let worker: Worker | null = null;

const handleMessage = (e: MessageEvent<WorkerResponse>) => {
  const msg = e.data;
  const task = pending.get(msg.id);
  if (!task) return;

  if (msg.type === "progress") {
    task.onProgress?.(msg.percent);
  } else if (msg.type === "success") {
    pending.delete(msg.id);
    task.resolve();
  } else if (msg.type === "error") {
    pending.delete(msg.id);
    task.reject(new Error(msg.message));
  }
};

const supportsOPFS = () => {
  return (
    typeof window !== "undefined" &&
    window.Worker &&
    "storage" in navigator &&
    "getDirectory" in navigator.storage
  );
};

const getWorker = () => {
  if (!supportsOPFS()) return null;
  if (!worker) {
    worker = new OpfsWorker();
    worker.onmessage = handleMessage;
  }
  return worker;
};

export function useOPFS(): UseOPFSResult {
  const isSupported = useMemo(supportsOPFS, []);

  const readFile = useCallback(async (fileName: string): Promise<File | null> => {
    try {
      const directory = await getOPFSDirectory();
      const fileHandle = await directory.getFileHandle(fileName);
      return await fileHandle.getFile();
    } catch (err) {
      LogSentryException({
        messagePrefix: "OPFS read file error",
        error: err instanceof Error ? err : new Error("Unknown OPFS file read error"),
      });
      return null;
    }
  }, []);

  const streamUrlToFile = useCallback(
    async (
      fileName: string,
      url: string,
      onProgress?: (progress: number) => void
    ): Promise<File | null> => {
      try {
        const id = fileName;
        const worker = getWorker();
        if (!worker) return null;

        await new Promise<void>((resolve, reject) => {
          pending.set(id, { resolve, reject, onProgress });
          worker.postMessage({ type: "stream", id, fileName, url });
        });

        return await readFile(fileName);
      } catch (err) {
        LogSentryException({
          messagePrefix: "OPFS stream URL to file error",
          error: err instanceof Error ? err : new Error("Unknown OPFS stream URL to file error"),
        });
        return null;
      }
    },
    [readFile]
  );

  const deleteFile = useCallback(async (fileName: string): Promise<void> => {
    try {
      const directory = await getOPFSDirectory();
      return directory.removeEntry(fileName);
    } catch (err) {
      if (!(err instanceof DOMException && err.name === "NotFoundError")) {
        LogSentryException({
          messagePrefix: "OPFS delete file error",
          error: err instanceof Error ? err : new Error("Unknown OPFS delete file error"),
        });
      }
    }
  }, []);

  return {
    isSupported,
    readFile,
    deleteFile,
    streamUrlToFile,
  };
}
